import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Check,
  ArrowRight,
  ArrowLeft,
  MessageCircle,
  Type,
  Globe,
  Gem,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

const steps = [
  { id: 1, title: 'Enter Text', icon: Type },
  { id: 2, title: 'Choose Language', icon: Globe },
  { id: 3, title: 'Select Style', icon: Gem },
  { id: 4, title: 'Special Notes', icon: FileText },
];

const languages = [
  { id: 'urdu', name: 'Urdu', nameNative: 'اردو', sample: 'بسم اللہ الرحمن الرحیم' },
  { id: 'arabic', name: 'Arabic', nameNative: 'العربية', sample: 'بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ' },
  { id: 'english', name: 'English', nameNative: 'English', sample: 'In the name of Allah' },
];

const decorStyles = [
  {
    id: 'pearl-classic',
    name: 'Pearl Classic',
    description: 'Elegant pearl embellishments with subtle shimmer',
    image: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=200&q=80',
  },
  {
    id: 'silver-thread',
    name: 'Silver Threadwork',
    description: 'Intricate silver thread calligraphy',
    image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=200&q=80',
  },
  {
    id: 'minimal-elegant',
    name: 'Minimal Elegant',
    description: 'Clean and sophisticated design',
    image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=200&q=80',
  },
  {
    id: 'floral-luxury',
    name: 'Floral Luxury',
    description: 'Delicate floral patterns with pearl accents',
    image: 'https://images.unsplash.com/photo-1594463750939-ebb28c3f7f75?w=200&q=80',
  },
];

export default function CustomizePage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { getProductById, addToCart } = useApp();
  const [currentStep, setCurrentStep] = useState(1);
  const [product, setProduct] = useState(null);

  const [formData, setFormData] = useState({
    customText: '',
    language: '',
    decorStyle: '',
    specialNotes: '',
  });

  useEffect(() => {
    if (productId) {
      const found = getProductById(productId);
      if (found) {
        setProduct(found);
      }
    }
  }, [productId, getProductById]);

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    if (product) {
      addToCart(product, 1, formData);
      toast.success('Customized order added to cart!');
      navigate('/cart');
    } else {
      // Open WhatsApp with customization details
      const message = encodeURIComponent(
        `Assalamu Alaikum! I would like to place a custom order:\n\nText: ${formData.customText}\nLanguage: ${formData.language}\nStyle: ${formData.decorStyle}\nNotes: ${formData.specialNotes || 'None'}\n\nPlease help me with this order.`
      );
      window.open(`https://wa.me/919111841033?text=${message}`, '_blank');
    }
  };

  const isStepComplete = (step) => {
    switch (step) {
      case 1:
        return formData.customText.length > 0;
      case 2:
        return formData.language.length > 0;
      case 3:
        return formData.decorStyle.length > 0;
      case 4:
        return true; // Special notes are optional
      default:
        return false;
    }
  };

  const canProceed = isStepComplete(currentStep);

  return (
    <div className="page-transition min-h-screen">
      {/* Header */}
      <div className="bg-gradient-burgundy-soft border-b border-border/30">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-silver/20 bg-card/30 backdrop-blur-sm mb-6">
              <Sparkles className="h-4 w-4 text-silver" />
              <span className="text-sm text-silver font-sans">Customization</span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-foreground mb-4">
              {product ? `Customize: ${product.name}` : 'Create Your Custom Order'}
            </h1>
            <p className="text-muted-foreground font-body max-w-md mx-auto">
              Personalize your piece with meaningful text and beautiful details
            </p>
          </motion.div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="sticky top-16 md:top-20 z-30 bg-background/95 backdrop-blur-md border-b border-border/30">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isComplete = isStepComplete(step.id) && currentStep > step.id;
              const isCurrent = currentStep === step.id;

              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => setCurrentStep(step.id)}
                    className={`flex flex-col items-center gap-1 transition-colors ${
                      isCurrent
                        ? 'text-silver-light'
                        : isComplete
                        ? 'text-success'
                        : 'text-muted-foreground'
                    }`}
                  >
                    <div
                      className={`h-10 w-10 rounded-full flex items-center justify-center border-2 transition-all ${
                        isCurrent
                          ? 'border-silver-light bg-silver/10'
                          : isComplete
                          ? 'border-success bg-success/10'
                          : 'border-border bg-muted'
                      }`}
                    >
                      {isComplete ? (
                        <Check className="h-5 w-5" />
                      ) : (
                        <Icon className="h-5 w-5" />
                      )}
                    </div>
                    <span className="text-xs font-medium hidden sm:block">
                      {step.title}
                    </span>
                  </button>

                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 rounded-full ${
                        isComplete ? 'bg-success' : 'bg-border'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-2xl mx-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: Enter Text */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h2 className="font-display text-xl text-silver-light mb-2">
                    What text would you like?
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Enter the duas, names, or meaningful text you want on your piece
                  </p>
                </div>

                <Textarea
                  variant="luxury"
                  placeholder="E.g., Bismillah, couple names, special dua, Quranic verse..."
                  value={formData.customText}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, customText: e.target.value }))
                  }
                  className="min-h-[150px] text-lg"
                />

                <div className="bg-card/50 rounded-xl p-4 border border-border/50">
                  <p className="text-sm text-muted-foreground font-body">
                    <span className="text-silver">Tip:</span> Popular choices include
                    Bismillah, Surah Al-Fatiha, couple names with marriage date, or
                    blessings for the newlyweds.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Step 2: Choose Language */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h2 className="font-display text-xl text-silver-light mb-2">
                    Select Language
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Choose the language for your calligraphy
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {languages.map((lang) => (
                    <button
                      key={lang.id}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, language: lang.id }))
                      }
                      className={`p-6 rounded-xl border text-center transition-all ${
                        formData.language === lang.id
                          ? 'border-silver-light bg-silver/10'
                          : 'border-border hover:border-silver/30 bg-card'
                      }`}
                    >
                      <span className="text-2xl font-body block mb-2" dir="rtl">
                        {lang.nameNative}
                      </span>
                      <span className="text-sm text-silver-light block mb-3">
                        {lang.name}
                      </span>
                      <span
                        className="text-xs text-muted-foreground font-body"
                        dir={lang.id !== 'english' ? 'rtl' : 'ltr'}
                      >
                        {lang.sample}
                      </span>
                      {formData.language === lang.id && (
                        <div className="mt-3">
                          <Check className="h-5 w-5 text-success mx-auto" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 3: Select Style */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h2 className="font-display text-xl text-silver-light mb-2">
                    Choose Decoration Style
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Select the embellishment style for your piece
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {decorStyles.map((style) => (
                    <button
                      key={style.id}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, decorStyle: style.id }))
                      }
                      className={`relative overflow-hidden rounded-xl border text-left transition-all ${
                        formData.decorStyle === style.id
                          ? 'border-silver-light'
                          : 'border-border hover:border-silver/30'
                      }`}
                    >
                      <div className="h-32 bg-muted">
                        <img
                          src={style.image}
                          alt={style.name}
                          className="h-full w-full object-cover opacity-60"
                        />
                      </div>
                      <div className="p-4 bg-card">
                        <h3 className="font-display text-silver-light mb-1">
                          {style.name}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {style.description}
                        </p>
                      </div>
                      {formData.decorStyle === style.id && (
                        <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-success flex items-center justify-center">
                          <Check className="h-4 w-4 text-success-foreground" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 4: Special Notes */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h2 className="font-display text-xl text-silver-light mb-2">
                    Any Special Requests?
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Add any additional notes or preferences (optional)
                  </p>
                </div>

                <Textarea
                  variant="luxury"
                  placeholder="E.g., specific placement, color preferences, deadline..."
                  value={formData.specialNotes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, specialNotes: e.target.value }))
                  }
                  className="min-h-[120px]"
                />

                {/* Summary */}
                <div className="bg-card rounded-xl p-6 border border-border/50 space-y-4">
                  <h3 className="font-display text-lg text-silver-light">Order Summary</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Text:</span>
                      <span className="text-foreground text-right max-w-[60%] truncate">
                        {formData.customText || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Language:</span>
                      <span className="text-foreground">
                        {languages.find((l) => l.id === formData.language)?.name || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Style:</span>
                      <span className="text-foreground">
                        {decorStyles.find((s) => s.id === formData.decorStyle)?.name || '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-border/30">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={currentStep === 1}
              className={currentStep === 1 ? 'invisible' : ''}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>

            {currentStep < 4 ? (
              <Button
                variant="ivory"
                onClick={handleNext}
                disabled={!canProceed}
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="whatsapp"
                onClick={handleSubmit}
                disabled={!formData.customText || !formData.language || !formData.decorStyle}
              >
                <MessageCircle className="h-4 w-4" />
                {product ? 'Add to Cart' : 'Submit via WhatsApp'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
